import isEmpty from 'lodash/isEmpty';
import { DateFormat } from 'common/types';
import { Days, days, getMinutes, getTripInfo } from 'common/utils';
import { DepartureTime } from 'list/common/filter/DepartureTimeFilter';
import {
  IFare,
  IFareDetail,
  IFareTypeList,
  IQueryValue,
  IReceivedInternationalResult,
  ISchFares,
  IScheduleDetail,
  ISchedules,
  SchFaresMap,
  ScheduleKeys,
} from './types';

export const toDays = (date: string, format = DateFormat.YMDHm) =>
  days(date, format);

export const hasAdultNaverFare = (fare: IFareDetail) =>
  +fare?.Adult?.NaverFare > 0;

export const getPriceOfPerson = (
  { Fare, QCharge, Tax, NaverFare }: IFare = {
    Fare: 0,
    QCharge: 0,
    Tax: 0,
    NaverFare: 0,
  },
  isNaverFare?: boolean,
) => {
  const price = Math.ceil(+NaverFare > 0 && isNaverFare ? +NaverFare : +Fare);
  return price + Number(QCharge) + Number(Tax); // Math.ceil issues/617
};

export const mergeSchedules = (stack: ISchedules[], cur: ISchedules[]) =>
  cur?.length
    ? cur.reduce(
        (arr, itinerary, index) => {
          const newItin = Object.entries(itinerary).reduce(
            (acc, [schId, schedule]) => ({ ...acc, [schId]: { ...schedule } }),
            {},
          );
          arr[index] = stack ? { ...stack[index], ...newItin } : newItin;
          return arr;
        },
        [...stack],
      )
    : stack;

export const mergeFares = (stack: ISchFares, fares: ISchFares) => {
  const fareEntries = Object.entries(fares) || [];
  if (!fareEntries.length) return stack;
  const overlapFares = fareEntries.reduce(
    (acc: any, [schFareKey, schFares]) => {
      const { fare: currentFare } = schFares;
      const stackSchFares = stack?.[schFareKey] || { fare: {} };
      const { fare: stackFare } = stackSchFares;

      Object.keys(currentFare).forEach(fareType => {
        const currentFareList: IFareDetail[] = currentFare[fareType];
        stackFare[fareType] = (
          stackFare[fareType]
            ? [...stackFare[fareType], ...currentFareList]
            : [...(currentFareList ?? [])]
        )
          // 동일 OTA로부터 동일 요금타입이 1개 이상 유입될 수 있음(e.g. 와이페이모어의 로컬요금, 호스트요금)
          // 우선 가격으로 정렬 시켜 놓고 중복제거
          .sort(
            (a: IFareDetail, b: IFareDetail) =>
              getPriceOfPerson(a.Adult, true) - getPriceOfPerson(b.Adult, true),
          )
          .reduce((accu: IFareDetail[], curr: IFareDetail) => {
            if (!accu.find(prev => prev.AgtCode === curr.AgtCode))
              accu.push(curr);
            return accu;
          }, []);
      });

      acc[schFareKey] = { ...stackSchFares, fare: stackFare };
      return acc;
    },
    {},
  );
  return { ...stack, ...overlapFares };
};

// 한글항목 디코딩
export const decodeValues = (obj: any) => {
  if (obj && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc: any, key: string) => {
      acc[key] = decodeURIComponent(obj[key]).replace(/\+/g, ' ');
      return acc;
    }, {});
  }
  return {};
};

export const defaultInternationalResult = {
  galileoFlag: true,
  galileoKey: '',
  travelBizFlag: true,
  travelBizKey: '',
  resCnt: 0,
  totalResCnt: 0,
  results: {
    airlines: {},
    airports: {},
    errors: [],
    fareTypes: {},
    fares: {},
    schedules: [],
    carbonEmissionAverage: {},
  },
};

export const mergeInternationalList = (
  prevList: IReceivedInternationalResult = defaultInternationalResult,
  curList: IReceivedInternationalResult,
) => {
  const { results: cur, totalResCnt, resCnt } = curList;
  const stack = { ...prevList.results };
  const cntSum =
    prevList.totalResCnt === totalResCnt && prevList.resCnt
      ? prevList.resCnt + resCnt
      : resCnt;
  if (cur && cur.schedules && cur.fares) {
    stack.airlines = { ...stack.airlines, ...decodeValues(cur.airlines) };
    stack.airports = { ...stack.airports, ...decodeValues(cur.airports) };
    stack.fareTypes = { ...stack.fareTypes, ...decodeValues(cur.fareTypes) };
    stack.schedules = mergeSchedules(stack.schedules, cur.schedules);
    stack.fares = mergeFares(stack.fares, cur.fares);
    stack.carbonEmissionAverage = isEmpty(stack.carbonEmissionAverage)
      ? cur.carbonEmissionAverage
      : stack.carbonEmissionAverage;
  }
  return {
    ...curList,
    results: stack,
    totalResCnt,
    resCnt: cntSum,
  };
};

export const getDepartureTimeValue = (value: Days) => {
  const hour = value.hour();
  const hourGap = Math.floor(hour / 3) * 3;
  if (hourGap < 6) return DepartureTime.T0;
  if (hourGap > 21) return DepartureTime.T21;
  return DepartureTime[`T${hourGap}` as keyof typeof DepartureTime];
};

const getFlightTime = (sch: IScheduleDetail[], journeyTime: string[]) =>
  sch.reduce<number>(
    (a, c) =>
      a +
      (journeyTime
        ? getMinutes(journeyTime)
        : getMinutes(c.ct) + getMinutes(c.jt)),
    0,
  );

const getTotalFlightTime = (
  schs: IScheduleDetail[][],
  journeyTimes: string[][],
) => schs.reduce<number>((a, c, i) => a + getFlightTime(c, journeyTimes[i]), 0);

export const getFareKeys = (fare: IFareTypeList) => {
  const fareKeys = Object.keys(fare).sort(
    (a, b) =>
      getPriceOfPerson(fare[a][0].Adult, true) -
      getPriceOfPerson(fare[b][0].Adult, true),
  );
  const indexOfA01 = fareKeys.indexOf('A01');
  if (indexOfA01 > 9) {
    fareKeys.splice(indexOfA01, 1);
    fareKeys.splice(9, 0, 'A01');
  }
  return fareKeys;
};

interface ILinkParams {
  router: any;
  method?: string;
  flights?: [string, string, string][];
  selectedFlight?: string;
  selectType: string;
  shallow?: boolean;
  callback?: Function;
}

export const getLink = ({
  selectedFlight,
  router,
  selectType,
}: ILinkParams) => {
  const {
    query: { routes, ...queryParams },
  } = router;
  const queries = new URLSearchParams({
    ...queryParams,
    selectedFlight,
    selectType,
  });
  if (!selectedFlight) {
    queries.delete('selectedFlight');
  }
  if (!selectType) queries.delete('selectType');
  queries.delete('samePriceOrgSch');
  const queryString = queries.toString();
  return {
    path: `/flights/international/[...routes]?${queryString}`,
    as: `/flights/international/${routes?.join('/')}?${queryString}`,
  };
};

export const routeChange = ({
  router,
  method = '',
  selectedFlight,
  selectType,
  shallow = true,
  callback,
}: ILinkParams) => {
  const { path, as } = getLink({ selectedFlight, router, selectType });
  router[method](path, as, { shallow }).then(callback);
};

export const setRoute = ({
  router,
  method,
  flights = [],
  selectType = 'indivisual',
  callback,
}: ILinkParams) => {
  const schString = flights
    .map(
      ([sch, fareType, airline], i) =>
        `${i + 1}:${sch}:${fareType}:HK:${airline}`,
    )
    .join(',');
  routeChange({
    router,
    method,
    selectedFlight: schString,
    selectType,
    callback,
  });
};

export const parseSelectedFlight = (selectedFlight: string) =>
  selectedFlight.split(',').reduce<[string, string, string][]>((acc, f) => {
    const [, sch, fareType, , airline] = f.split(':');
    acc.push([sch, fareType, airline]);
    return acc;
  }, []);

export const getTripStringFromRoutes = (routes: string[]) => {
  return getTripInfo(
    routes.map(r => {
      const [arrivalAirport, departureAirport] = r.split('-');
      return { arrivalAirport, departureAirport, departureDate: '' };
    }),
  );
};

export const getWatchingQueryValues = ({
  fareType,
  isDirect,
  itinerary,
  adult,
  child,
  infant,
}: IQueryValue) =>
  JSON.stringify([fareType, isDirect, itinerary, adult, child, infant]);

export const getSchedule = (
  sch: string,
  schedules: ISchedules[],
  index: number = 0,
) => schedules[index]?.[sch.split('|')[index]];

export const getDateTime = (
  sch: string,
  schedules: ISchedules[],
  index: number = 0,
) =>
  Number(
    getSchedule(sch, schedules, index)?.detail[0][ScheduleKeys.startDateTime],
  );
export const getDuration = (
  sch: string,
  schedules: ISchedules[],
  index: number = 0,
) => {
  return getMinutes(getSchedule(sch, schedules, index)?.journeyTime);
};

export const getTotalDuration = (sch: string, schedules: ISchedules[]) =>
  sch.split('|').reduce((acc, s, i) => acc + getDuration(sch, schedules, i), 0);

interface SchInfos {
  lowestPrice: number;
  time: number;
  schedule: IScheduleDetail[][];
  sdt: Days;
}

const getLowestFareKey = (fare: IFareTypeList) => {
  return Object.entries(fare).sort(([, aVal], [, bVal]) => {
    return (
      getPriceOfPerson(aVal[0].Adult, true) -
      getPriceOfPerson(bVal[0].Adult, true)
    );
  })[0][0];
};
export const getInfosFromSch = (
  sch: string,
  fares: SchFaresMap,
  schedules: ISchedules[],
): SchInfos => {
  const [depSch, arrSch] = sch.split('|');
  const { detail: depDetail, journeyTime: depJT } =
    schedules[0]?.[depSch] || {};
  const { detail: arrDetail, journeyTime: arrJT } =
    schedules[1]?.[arrSch] || {};
  if (!depDetail || !arrDetail)
    return {
      lowestPrice: Number.MAX_SAFE_INTEGER,
      time: Number.MAX_SAFE_INTEGER,
      schedule: [],
      sdt: days(),
    };

  const schedule = [depDetail, arrDetail];
  const fare = fares.get(sch) || {};
  const lowestFareKey = getLowestFareKey(fare);
  const price = getPriceOfPerson(fare[lowestFareKey]?.[0].Adult, true);
  return {
    lowestPrice: price || Number.MAX_SAFE_INTEGER,
    time: getTotalFlightTime(schedule, [depJT, arrJT]),
    sdt: toDays(schedule[0][0].sdt),
    schedule,
  };
};

/**
 * TODO: 추후 서버에서 validation 되도록 국제선 이관 시 적용
 * 기존 코드에 최대한 영향이 없도록 작성하기 위해
 * 여정1, 여정2를 비교할 수 없는 환경에서는 기존과 동일하게
 * 필더링 되도록 true 반환
 */
export const isValidArrivalSchedule = (
  schedules: ISchedules[],
  departureScheduleKey: string,
  arrivalScheduleKey: string,
  selectedFlightCount?: number,
) => {
  if (
    !schedules ||
    (schedules.length < 2 && (!selectedFlightCount || selectedFlightCount > 0))
  ) {
    return true;
  }

  const scheduleIndex = selectedFlightCount || 1;

  const [departureSchedule, arrivalSchedule] = [
    schedules[scheduleIndex - 1],
    schedules[scheduleIndex],
  ];
  const departureDetail =
    departureSchedule[departureScheduleKey]?.detail?.at(-1);
  const arrivalDetail = arrivalSchedule[arrivalScheduleKey]?.detail?.[0];

  if (
    !departureDetail ||
    !arrivalDetail ||
    departureDetail.ea !== arrivalDetail.sa
  ) {
    return true;
  }

  const departureEndDateTime = toDays(departureDetail.edt).toDate();
  const arrivalStartDateTime = toDays(arrivalDetail.sdt).toDate();

  if (!departureEndDateTime || !arrivalStartDateTime) {
    return true;
  }

  return departureEndDateTime < arrivalStartDateTime;
};
